import React, { useMemo, useState, useRef, useEffect } from 'react';
import { usePracticeContent } from '../hooks/usePracticeContent';
import { INDIA_VIEWBOX, INDIA_DOT_FIELD, latLngToSvg } from '../lib/indiaProjection';

const NAVY = '#002F6C';
const OLIVE = '#5F6B2E';
const GRID = '#BDB9B1';
const BG = '#F8F8F7';

// Cities that get the standing "home" treatment (olive dot + always-on ring/label).
// Case-insensitive; extensible.
const HOME = new Set(['ahmedabad']);

// Fallback so the map is never empty/blank if the fetch fails.
const SEED_INDIA = [
  { name: 'Ahmedabad', project_count: 11, lat: 23.0225, lng: 72.5714 },
  { name: 'Delhi NCR', project_count: 7, lat: 28.6139, lng: 77.2090 },
  { name: 'Mumbai', project_count: 6, lat: 19.0760, lng: 72.8777 },
  { name: 'Jaipur', project_count: 5, lat: 26.9124, lng: 75.7873 },
  { name: 'Udaipur', project_count: 5, lat: 24.5854, lng: 73.7125 },
  { name: 'Surat', project_count: 5, lat: 21.1702, lng: 72.8311 },
  { name: 'Indore', project_count: 4, lat: 22.7196, lng: 75.8577 },
  { name: 'Pune', project_count: 4, lat: 18.5204, lng: 73.8567 },
  { name: 'Bengaluru', project_count: 4, lat: 12.9716, lng: 77.5946 },
  { name: 'Hyderabad', project_count: 3, lat: 17.3850, lng: 78.4867 },
  { name: 'Chennai', project_count: 3, lat: 13.0827, lng: 80.2707 },
  { name: 'Kolkata', project_count: 3, lat: 22.5726, lng: 88.3639 },
];
const SEED_INTL = [
  { name: 'Kenya' }, { name: 'DR Congo' }, { name: 'Namibia' }, { name: 'Rwanda' },
];

// Snap an SVG point to the nearest dot-field node (min squared distance),
// so every marker reads as a lit grid dot rather than a floating pin.
const snapToNode = (x, y) => {
  let best = INDIA_DOT_FIELD[0];
  let bestD = Infinity;
  for (let i = 0; i < INDIA_DOT_FIELD.length; i += 1) {
    const px = INDIA_DOT_FIELD[i][0];
    const py = INDIA_DOT_FIELD[i][1];
    const d = (px - x) * (px - x) + (py - y) * (py - y);
    if (d < bestD) { bestD = d; best = INDIA_DOT_FIELD[i]; }
  }
  return { x: best[0], y: best[1] };
};

const PracticeMap = () => {
  const { data } = usePracticeContent();
  const india = data && data.india && data.india.length ? data.india : SEED_INDIA;
  const international = data && data.international && data.international.length ? data.international : SEED_INTL;

  const [active, setActive] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const svgRef = useRef(null);

  // One-time reveal when the map scrolls into view (skipped under reduced-motion,
  // which renders everything immediately).
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setRevealed(true);
      return undefined;
    }
    const el = svgRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { setRevealed(true); io.disconnect(); }
      });
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const markers = useMemo(() => {
    const maxCount = Math.max(...india.map((c) => c.project_count || 0), 1);
    return india.map((c) => {
      const p = latLngToSvg(c.lat, c.lng);
      const node = snapToNode(p.x, p.y);
      const count = c.project_count || 0;
      const rr = maxCount <= 1
        ? 8
        : 8 + 18 * Math.sqrt(Math.max(0, count - 1) / (maxCount - 1));
      return { name: c.name, x: node.x, y: node.y, rr, isHome: HOME.has(String(c.name).toLowerCase()) };
    });
  }, [india]);

  // per-marker entrance delay, radiating outward from the home marker
  const delays = useMemo(() => {
    const home = markers.find((m) => m.isHome) || markers[0];
    if (!home) return [];
    return markers.map((m) => {
      const d = Math.hypot(m.x - home.x, m.y - home.y);
      return 0.35 + (d / 900) * 1.1;
    });
  }, [markers]);

  // Render order: keep the active marker last so its ring/label paints on top.
  const order = markers.map((_, i) => i);
  if (active != null) {
    const idx = order.indexOf(active);
    if (idx > -1) { order.splice(idx, 1); order.push(active); }
  }

  return (
    <section className="py-24" data-testid="practice-map-section" style={{ backgroundColor: BG }}>
      <style>{`
        .pm-beyond-row{ display:flex; flex-wrap:wrap; align-items:baseline; gap:10px 22px;
          border-top:1px solid rgba(58,53,48,.18); padding-top:16px; }
        .pm-beyond-label{ font-family:Inter, sans-serif; font-weight:600; font-size:11px;
          letter-spacing:.24em; text-transform:uppercase; color:#4A6A9C; }
        .pm-beyond-names{ display:flex; flex-wrap:wrap; align-items:baseline; gap:8px;
          font-family:Lato, sans-serif; font-weight:400; color:#3A3530; text-transform:lowercase; }
        .pm-beyond-sep{ color:rgba(58,53,48,.4); }
        /* radial marker pop — paused until the map scrolls into view, fires once */
        @keyframes pmPop{ from{ opacity:0; transform:scale(.2); } to{ opacity:1; transform:scale(1); } }
        .pm-pop{ opacity:0; transform-box:view-box; animation:pmPop 450ms cubic-bezier(0.34,1.56,0.64,1) forwards;
          animation-delay:var(--pop-d,0s); animation-play-state:paused; }
        .pm-on .pm-pop{ animation-play-state:running; }
        /* single standing pulse on the home marker */
        @keyframes pmPulse{ 0%{ transform:scale(.55); opacity:.4; } 100%{ transform:scale(2.1); opacity:0; } }
        .pm-pulse{ transform-box:fill-box; transform-origin:center; pointer-events:none;
          animation:pmPulse 3s ease-out infinite; }
        @media (prefers-reduced-motion: reduce){
          .pm-pop{ opacity:1; animation:none; }
          .pm-pulse{ animation:none; opacity:0; }
        }
      `}</style>
      <div className="pr-spine">
        {/* intro copy (unchanged) */}
        <h2 className="text-2xl text-[#002F6C] lowercase mb-6" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}>
          where we work
        </h2>
        <p className="text-gray-600 lowercase leading-relaxed max-w-2xl mb-14" style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300 }}>
          from our studio in ahmedabad, our landscapes take root across india — and, increasingly, beyond it.
        </p>

        {/* dotted india map */}
        <div className="w-full max-w-2xl mx-auto">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${INDIA_VIEWBOX.w} ${INDIA_VIEWBOX.h}`}
            className="w-full h-auto"
            data-testid="practice-map-svg"
            role="img"
            aria-label="map of where studio 2+2 works across india and beyond"
            onClick={() => setActive(null)}
          >
            {/* field: static grid dots, one-time opacity reveal on the whole group */}
            <g
              data-testid="practice-map-field"
              style={{ opacity: revealed ? 1 : 0, transition: 'opacity .8s ease' }}
            >
              {INDIA_DOT_FIELD.map(([x, y], i) => (
                <circle key={`f${i}`} cx={x} cy={y} r={1.9} fill={GRID} opacity={0.85} />
              ))}
            </g>

            {/* markers: fade in once after the field, then static */}
            <g className={revealed ? 'pm-on' : undefined}>
              {order.map((i) => {
                const m = markers[i];
                const on = active === i;
                const dotR = m.isHome ? 3.2 : (on ? 3.2 : 2.8);
                const ringStroke = m.isHome ? OLIVE : NAVY;
                const ringOpacity = m.isHome ? (on ? 0.85 : 0.5) : (on ? 0.85 : 0);
                const labelFill = m.isHome ? OLIVE : NAVY;
                const labelOpacity = m.isHome ? 0.9 : (on ? 1 : 0);
                return (
                  <g
                    key={`m${i}`}
                    className="pm-pop"
                    data-testid={`practice-map-marker-${i}`}
                    data-name={m.name}
                    style={{ cursor: 'pointer', '--pop-d': `${delays[i]}s`, transformOrigin: `${m.x}px ${m.y}px` }}
                    onMouseEnter={() => setActive(i)}
                    onMouseLeave={() => setActive((a) => (a === i ? null : a))}
                    onClick={(e) => { e.stopPropagation(); setActive(i); }}
                  >
                    {m.isHome && (
                      <circle
                        className="pm-pulse"
                        cx={m.x}
                        cy={m.y}
                        r={m.rr}
                        fill="none"
                        stroke={OLIVE}
                        strokeWidth={1.2}
                        data-testid="practice-map-home-pulse"
                      />
                    )}
                    {/* generous transparent hit area for hover/tap */}
                    <circle cx={m.x} cy={m.y} r={Math.max(m.rr, 12)} fill="transparent" />
                    <circle
                      cx={m.x}
                      cy={m.y}
                      r={m.rr}
                      fill="none"
                      stroke={ringStroke}
                      strokeWidth={1.4}
                      opacity={ringOpacity}
                      style={{ transition: 'opacity .25s ease' }}
                    />
                    {m.isHome ? (
                      <g data-testid="practice-map-home-crosshair">
                        <line x1={m.x - 7} y1={m.y} x2={m.x + 7} y2={m.y} stroke={OLIVE} strokeWidth={1.6} />
                        <line x1={m.x} y1={m.y - 7} x2={m.x} y2={m.y + 7} stroke={OLIVE} strokeWidth={1.6} />
                      </g>
                    ) : (
                      <circle
                        cx={m.x}
                        cy={m.y}
                        r={dotR}
                        fill={NAVY}
                        style={{ transition: 'r .2s ease' }}
                      />
                    )}
                    <text
                      x={m.x + m.rr + 4}
                      y={m.y + 4}
                      fill={labelFill}
                      opacity={labelOpacity}
                      fontSize="12"
                      style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, paintOrder: 'stroke', transition: 'opacity .25s ease' }}
                      stroke={BG}
                      strokeWidth={3}
                    >
                      {String(m.name).toLowerCase()}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        </div>

        {/* and beyond india — tied to a hairline rule with the names in one row */}
        <div className="mt-14" data-testid="practice-map-beyond">
          <div className="pm-beyond-row">
            <span className="pm-beyond-label">and beyond india</span>
            <span className="pm-beyond-names">
              {international.map((c, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="pm-beyond-sep">·</span>}
                  <span data-testid={`practice-intl-name-${i}`}>{String(c.name).toLowerCase()}</span>
                </React.Fragment>
              ))}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PracticeMap;
